// The "Heute" row (workspace-start M2): up to four tiles above the module bands
// answering "what is now?" — a pure projection of the modules' `/user-dashboard`
// answers, never composed per persona (see the brief's Outcome invariants).
import { normalizeFactKey, type ModuleDashboard, type UpcomingItem } from './homebase';

// Mirrors ats-api/src/user-dashboard/activity-feed.ts:105 (ACTIVITY_FEED_MAX_ITEMS).
// No cross-package import (ADR 0001) — the workspace only needs the number to
// know when a news list is at its cap, not the module that enforces it.
export const ACTIVITY_FEED_MAX_ITEMS = 20;

// The three facts that make "Braucht mich" appear (Outcome invariants). Keys are
// in their NORMALISED (dotted) form — see `normalizeFactKey`.
export const ATTENTION_FACT_KEYS = ['ats.open_tasks', 'time.pending_approvals', 'apprentice.waiting'] as const;

export const HEUTE_TILE_KINDS = ['clock', 'date', 'attention', 'news', 'leave'] as const;
export type HeuteTileKind = (typeof HEUTE_TILE_KINDS)[number];

export interface HeuteClockTile {
  kind: 'clock';
  moduleKey: string;
  state: 'clocked_in' | 'clocked_out' | 'project_running';
  since: string | null;
}
export interface HeuteDateTile {
  kind: 'date';
  moduleKey: string;
  upcoming: UpcomingItem;
}
export interface HeuteAttentionChip {
  moduleKey: string;
  /** The envelope's `module` string — feeds `normalizeFactKey` for the label lookup. */
  dashboardModule: string;
  factKey: string;
  label: string;
  value: number | string;
  deepLink?: string;
}
export interface HeuteAttentionTile {
  kind: 'attention';
  total: number;
  chips: HeuteAttentionChip[];
}
export interface HeuteNewsTile {
  kind: 'news';
  total: number;
  /** True when any one source list sits at the cap — the count is then a floor, not a fact. */
  capped: boolean;
  moduleKeys: string[];
}
export interface HeuteLeaveTile {
  kind: 'leave';
  moduleKey: string;
  value: number | string;
  deepLink?: string;
}
export type HeuteTile = HeuteClockTile | HeuteDateTile | HeuteAttentionTile | HeuteNewsTile | HeuteLeaveTile;

type DashboardEntry = readonly [moduleKey: string, dashboard: ModuleDashboard];

function presentEntries(dashboards: Record<string, ModuleDashboard | null>, order: readonly string[]): DashboardEntry[] {
  return order
    .map((moduleKey) => [moduleKey, dashboards[moduleKey]] as const)
    .filter((e): e is DashboardEntry => !!e[1]);
}

function findClockTile(entries: DashboardEntry[]): HeuteClockTile | null {
  const found = entries.find(([, d]) => !!d.clock);
  if (!found) return null;
  const [moduleKey, d] = found;
  return { kind: 'clock', moduleKey, state: d.clock!.state, since: d.clock!.since };
}

function findDateTile(entries: DashboardEntry[]): HeuteDateTile | null {
  let best: HeuteDateTile | null = null;
  for (const [moduleKey, d] of entries) {
    if (!d.upcoming) continue;
    // Ties keep the earliest MODULE encountered (`entries` is already in module
    // order) — only a strictly earlier date replaces the current pick.
    if (!best || new Date(d.upcoming.at).getTime() < new Date(best.upcoming.at).getTime()) {
      best = { kind: 'date', moduleKey, upcoming: d.upcoming };
    }
  }
  return best;
}

function findAttentionTile(entries: DashboardEntry[]): HeuteAttentionTile | null {
  const chips: HeuteAttentionChip[] = [];
  for (const [moduleKey, d] of entries) {
    for (const f of d.keyFacts) {
      const normalized = normalizeFactKey(d.module, f.key);
      if ((ATTENTION_FACT_KEYS as readonly string[]).includes(normalized)) {
        chips.push({ moduleKey, dashboardModule: d.module, factKey: f.key, label: f.label, value: f.value, deepLink: f.deepLink });
      }
    }
  }
  if (!chips.length) return null;
  const total = chips.reduce((sum, c) => sum + (typeof c.value === 'number' && Number.isFinite(c.value) ? c.value : 0), 0);
  return { kind: 'attention', total, chips };
}

function findNewsTile(entries: DashboardEntry[]): HeuteNewsTile | null {
  const withNews = entries.filter((e): e is DashboardEntry => Array.isArray(e[1].news));
  if (!withNews.length) return null;
  let total = 0;
  let capped = false;
  const moduleKeys: string[] = [];
  for (const [moduleKey, d] of withNews) {
    const len = d.news!.length;
    total += len;
    if (len >= ACTIVITY_FEED_MAX_ITEMS) capped = true;
    moduleKeys.push(moduleKey);
  }
  return { kind: 'news', total, capped, moduleKeys };
}

function findLeaveTile(entries: DashboardEntry[]): HeuteLeaveTile | null {
  for (const [moduleKey, d] of entries) {
    const f = d.keyFacts.find((kf) => normalizeFactKey(d.module, kf.key) === 'time.leave_remaining');
    if (f) return { kind: 'leave', moduleKey, value: f.value, deepLink: f.deepLink };
  }
  return null;
}

/**
 * Turns the fan-out result into the Heute row's tiles. Fixed order (clock, date,
 * attention, news, leave); at most four render; fewer than two candidates means
 * no row at all (Outcome invariants) — the caller renders nothing rather than a
 * one-tile row.
 */
export function composeHeuteRow(dashboards: Record<string, ModuleDashboard | null>, order: readonly string[]): HeuteTile[] {
  const entries = presentEntries(dashboards, order);
  const candidates = [findClockTile(entries), findDateTile(entries), findAttentionTile(entries), findNewsTile(entries), findLeaveTile(entries)].filter(
    (t): t is HeuteTile => t !== null,
  );
  if (candidates.length < 2) return [];
  return candidates.slice(0, 4);
}

function sameCalendarDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function sameUtcCalendarDay(a: Date, b: Date): boolean {
  return a.getUTCFullYear() === b.getUTCFullYear() && a.getUTCMonth() === b.getUTCMonth() && a.getUTCDate() === b.getUTCDate();
}

/**
 * The date tile's big text: same calendar day as `now` → the time; a later day →
 * the date; with `endAt` on another day → the span "12.10.–16.10." (en dash, no
 * spaces). The workspace never computes a day count — it only formats what the
 * module already resolved to a date.
 *
 * All-day items (leave, public holidays) are serialised as UTC midnight by
 * their owning module (e.g. time-api's `new Date(startDate).toISOString()`) —
 * reading/formatting them with LOCAL getters lands on the previous calendar
 * day anywhere west of UTC. So `allDay` items are read and formatted entirely
 * in UTC, and always show a date, never a time — even when that UTC day is
 * also "today" locally. Timed items (interviews, exams) keep local
 * wall-clock behaviour throughout.
 */
export function formatUpcomingBigText(item: UpcomingItem, now: Date, locale: string): string {
  const at = new Date(item.at);
  if (item.allDay) {
    const dateFmt = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'numeric', timeZone: 'UTC' });
    if (item.endAt) {
      const end = new Date(item.endAt);
      if (!sameUtcCalendarDay(at, end)) {
        return `${dateFmt.format(at)}–${dateFmt.format(end)}`;
      }
    }
    return dateFmt.format(at);
  }
  if (sameCalendarDay(at, now)) {
    return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(at);
  }
  const dateFmt = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'numeric' });
  if (item.endAt) {
    const end = new Date(item.endAt);
    if (!sameCalendarDay(at, end)) {
      return `${dateFmt.format(at)}–${dateFmt.format(end)}`;
    }
  }
  return dateFmt.format(at);
}
